import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button, EmptyState, PageArticle } from "@generalapplications/ui";

export type RootErrorBoundaryProps = {
  children: ReactNode;
};

type RootErrorBoundaryState = {
  hasError: boolean;
};

// Last line of defense around the whole app. Without this, any uncaught
// render/hydration error (e.g. React error #418 — "Hydration failed
// because the initial UI does not match what was rendered on the server")
// unmounts the entire tree with nothing to catch it, leaving a blank page —
// exactly what happened 2026-08-19 with no error boundary anywhere in this
// app. This doesn't fix whatever throws; it stops one bad render from
// taking down content that has nothing to do with it, and gives a visitor
// a way back instead of a blank screen.
export class RootErrorBoundary extends Component<
  RootErrorBoundaryProps,
  RootErrorBoundaryState
> {
  override state: RootErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RootErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // console.error is allowed by this repo's lint config (only console.log
    // is disallowed); last-resort visibility until an error-reporting
    // service is wired up.
    console.error("RootErrorBoundary caught:", error, info.componentStack);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <PageArticle centered>
          <EmptyState
            titleAs="h1"
            title="Something went wrong loading this page."
          >
            {/* Outside the router: the library draws a plain link, so this
                reloads the document rather than re-rendering the broken tree. */}
            <Button href="/" variant="outline">
              Reload from the homepage
            </Button>
          </EmptyState>
        </PageArticle>
      );
    }

    return this.props.children;
  }
}
